import { motion } from "framer-motion";
import { Download, MousePointerClick, Rocket } from "lucide-react";
import { Check } from "lucide-react";
import { STEPS, PLATFORMS } from "../../lib/landing-data";

const STEP_ICONS = [Download, MousePointerClick, Rocket];

export const HowItWorks = () => {
  return (
    <section id="how" className="relative py-20 sm:py-28 bg-gradient-to-b from-purple-50/40 via-white to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto mb-14"
        >
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-100 text-purple-700 text-xs font-bold uppercase tracking-wider">
            How it works
          </span>
          <h2 className="font-heading mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight">
            Get started in under 2 minutes
          </h2>
          <p className="mt-4 text-slate-600 text-base sm:text-lg">
            No setup, no account, no learning curve.
          </p>
        </motion.div>

        <div className="relative grid md:grid-cols-3 gap-6 lg:gap-8">
          <div className="hidden md:block absolute top-16 left-[16%] right-[16%] h-px bg-gradient-to-r from-transparent via-purple-300 to-transparent" />
          {STEPS.map((s, i) => {
            const Icon = STEP_ICONS[i];
            return (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.12 }}
                data-testid={`step-card-${i}`}
                className="relative rounded-3xl border border-purple-100 bg-white/80 backdrop-blur p-7 hover:shadow-xl hover:shadow-purple-600/5 transition-all"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="relative">
                    <div className="w-14 h-14 rounded-2xl bg-white border-2 border-purple-200 flex items-center justify-center text-purple-700 shadow-md">
                      <Icon className="w-6 h-6" />
                    </div>
                    <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-purple-600 text-white text-xs font-bold flex items-center justify-center shadow shadow-purple-600/40">
                      {i + 1}
                    </span>
                  </div>
                  <div className="font-heading text-sm font-bold uppercase tracking-widest text-purple-600">
                    Step {s.n}
                  </div>
                </div>
                <h3 className="font-heading text-xl font-extrabold text-slate-900">{s.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export const Platforms = () => {
  return (
    <section id="platforms" className="relative py-20 sm:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto mb-14"
        >
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-100 text-purple-700 text-xs font-bold uppercase tracking-wider">
            Platforms supported
          </span>
          <h2 className="font-heading mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight">
            Native support for India&rsquo;s two biggest seller platforms
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6">
          {PLATFORMS.map((p, i) => (
            <motion.div
              key={p.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              data-testid={`platform-card-${i}`}
              className="relative rounded-3xl border border-slate-200 bg-white p-7 sm:p-9 hover:shadow-2xl hover:shadow-purple-600/5 hover:-translate-y-1 transition-all"
            >
              <div className={`absolute inset-x-0 top-0 h-32 bg-gradient-to-b ${p.accent} rounded-t-3xl opacity-50`} />
              <div className="relative">
                <div className="flex items-center gap-3 mb-2">
                  <span className={`w-3 h-3 rounded-full ${p.dot} shadow-lg`} />
                  <h3 className="font-heading text-2xl font-extrabold text-slate-900">{p.name}</h3>
                </div>
                <p className="text-sm text-slate-500">Activated automatically when you open the seller panel.</p>
                <ul className="mt-6 space-y-3">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm sm:text-base text-slate-700">
                      <span className="mt-0.5 w-5 h-5 shrink-0 rounded-full bg-purple-600 text-white flex items-center justify-center shadow shadow-purple-600/30">
                        <Check className="w-3 h-3" strokeWidth={3} />
                      </span>
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
